import { Outlet, NavLink } from 'react-router-dom';
import {
  Activity,
  Archive,
  BadgeCheck,
  Bot,
  Cloud,
  FileSearch,
  FileText,
  FlaskConical,
  Landmark,
  Layers,
  LayoutDashboard,
  LifeBuoy,
  LogOut,
  Megaphone,
  Menu,
  Scale,
  Settings as SettingsIcon,
  ShieldAlert,
  ShieldCheck,
  Ticket,
  UserPlus,
  User,
  UserMinus,
  UserX,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useState } from 'react';
import { config, getEnvironmentColor } from '../config';
import { useAuth } from './AuthProvider';
import { useOverviewBadges } from '../hooks/useAdminOps';
import {
  usePendingApprovalRequestCount,
  useReadyToPublishCount,
} from '../hooks/useApprovalRequests';
import type { OverviewQueueKey, QueueBadge } from '../api/adminOps';
import { AUDIT_RETENTION_READ_GROUPS } from '../pages/auditRetention.logic';

type NavSection = 'Content' | 'Operations' | 'Trust & Safety';

const NAV_SECTIONS: NavSection[] = ['Content', 'Operations', 'Trust & Safety'];

interface NavItem {
  name: string;
  href: string;
  icon: LucideIcon;
  section: NavSection;
  /** Cognito groups allowed to see this entry; omitted = any authenticated admin. */
  permission?: string[];
  /** Overview queue counts summed into this entry's sidebar badge. */
  badgeKeys?: OverviewQueueKey[];
}

const navigation: NavItem[] = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, section: 'Content' },
  { name: 'Experiments', href: '/experiments', icon: FlaskConical, section: 'Content' },
  { name: 'Dynamic Content', href: '/dynamic-content', icon: FileText, section: 'Content' },
  { name: 'CDN Files', href: '/cdn', icon: Cloud, section: 'Content' },
  { name: 'MCP Integration', href: '/mcp', icon: Bot, section: 'Content' },
  { name: 'Settings', href: '/settings', icon: SettingsIcon, section: 'Content' },
  { name: 'Overview', href: '/operations', icon: Activity, section: 'Operations' },
  {
    name: 'Document Review',
    href: '/document-review',
    icon: FileSearch,
    section: 'Operations',
    badgeKeys: ['ocr_manual_review'],
  },
  {
    name: 'Fraud Review',
    href: '/fraud-review',
    icon: ShieldAlert,
    section: 'Operations',
    badgeKeys: ['fraud_review'],
  },
  {
    name: 'Organizations',
    href: '/organizations',
    icon: Landmark,
    section: 'Operations',
    badgeKeys: ['irs_reviews_pending'],
  },
  // DM-7976. No badgeKeys: a sidebar count needs an overview key for the
  // verification queue, which is backend work. Visible to every admin group;
  // the page itself gates the decision form.
  {
    name: 'Charity Verification',
    href: '/charity-verifications',
    icon: BadgeCheck,
    section: 'Operations',
  },
  {
    name: 'Support',
    href: '/support',
    icon: LifeBuoy,
    section: 'Operations',
    badgeKeys: ['support_new'],
  },
  {
    name: 'Approvals',
    href: '/approvals',
    icon: ShieldCheck,
    section: 'Operations',
    badgeKeys: ['approvals_pending'],
  },
  // DM-6208. No badgeKeys: a sidebar count needs a matching key from the
  // overview endpoint, which is backend work rather than a UI-only change.
  { name: 'Broadcasts', href: '/broadcasts', icon: Megaphone, section: 'Operations' },
  // DM-7490. No badgeKeys: nothing about a partner code needs an operator's attention.
  { name: 'Referral Codes', href: '/referral-codes', icon: Ticket, section: 'Operations' },
  // DM-7703. No badgeKeys: new Founding Partner requests are counted on the
  // page itself; a sidebar count needs an overview key, which is backend work.
  { name: 'Website Leads', href: '/website-leads', icon: UserPlus, section: 'Operations' },
  // DM-7185. No badgeKeys for the same reason: deletion_requests_active counts
  // deletions in flight (Compliance), not requests made from the website.
  {
    name: 'Deletion Requests',
    href: '/deletion-requests',
    icon: UserMinus,
    section: 'Operations',
  },
  {
    name: 'Queues',
    href: '/operations/queues',
    icon: Layers,
    section: 'Operations',
    badgeKeys: ['quarantine', 'outbox_failed', 'avatars_stranded'],
  },
  {
    name: 'Compliance',
    href: '/compliance',
    icon: Scale,
    section: 'Operations',
    badgeKeys: ['deletion_requests_active'],
  },
  {
    name: 'Audit Retention',
    href: '/audit-retention',
    icon: Archive,
    section: 'Operations',
    permission: [...AUDIT_RETENTION_READ_GROUPS],
  },
  {
    name: 'Moderation',
    href: '/moderation',
    icon: ShieldAlert,
    section: 'Trust & Safety',
    badgeKeys: ['reports_pending', 'flagged_content'],
  },
  { name: 'Enforcement', href: '/enforcement', icon: UserX, section: 'Trust & Safety' },
];

function badgeCountFor(
  item: NavItem,
  queues: Record<OverviewQueueKey, QueueBadge> | undefined
): { count: number; truncated: boolean } | undefined {
  if (!item.badgeKeys || !queues) return undefined;
  let count = 0;
  let truncated = false;
  for (const key of item.badgeKeys) {
    const badge = queues[key];
    if (!badge) continue;
    count += badge.count;
    truncated = truncated || badge.truncated;
  }
  return { count, truncated };
}

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { user, signOut, hasRole } = useAuth();

  const visibleNavigation = navigation.filter(
    item => !item.permission || hasRole(...item.permission)
  );
  const wantsBadges = visibleNavigation.some(item => item.badgeKeys && item.badgeKeys.length > 0);
  // Badge counts are best-effort: if the overview fetch fails, badges simply don't render.
  const { data: overview } = useOverviewBadges(wantsBadges);
  const queues = overview?.data.queues;
  // DM-6437: content/experiment/CDN approval requests live on the content API,
  // not in the overview endpoint, so the Approvals badge adds them here.
  const { data: pendingApprovalRequests } = usePendingApprovalRequestCount(wantsBadges);
  // DM-7214: nothing notifies a requester that their revision was approved,
  // so the page they publish it from carries the count.
  const { data: readyToPublish } = useReadyToPublishCount(user?.sub, wantsBadges);

  const handleSignOut = async () => {
    await signOut();
  };

  return (
    <div className="min-h-screen bg-dark">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-dark-card border-r border-gray-border transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-gray-border">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <span className="text-white font-bold text-sm">DM</span>
              </div>
              <div>
                <h1 className="text-lg font-semibold text-white">DonateMate</h1>
                <p className="text-xs text-gray-text">CMS Admin</p>
              </div>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-gray-text hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Environment Badge */}
          <div className="px-6 py-3">
            <div
              className="px-3 py-2 rounded-lg text-xs font-medium text-white text-center"
              style={{ backgroundColor: getEnvironmentColor() }}
            >
              {config.environment.toUpperCase()} ENVIRONMENT
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-4 space-y-4 custom-scrollbar overflow-y-auto">
            {NAV_SECTIONS.map(section => {
              const items = visibleNavigation.filter(item => item.section === section);
              if (items.length === 0) return null;
              return (
                <div key={section} className="space-y-1">
                  <p className="px-3 text-xs font-semibold uppercase tracking-wider text-gray-text/70">
                    {section}
                  </p>
                  {items.map(item => {
                    const overviewBadge = badgeCountFor(item, queues);
                    const requestCount =
                      item.href === '/approvals' ? (pendingApprovalRequests ?? 0) : 0;
                    const badge =
                      overviewBadge || requestCount > 0
                        ? {
                            count: (overviewBadge?.count ?? 0) + requestCount,
                            truncated: overviewBadge?.truncated ?? false,
                          }
                        : undefined;
                    return (
                      <NavLink
                        key={item.name}
                        to={item.href}
                        className={({ isActive }) =>
                          `flex items-center space-x-3 px-3 py-2.5 rounded-xl transition-all duration-200 ${
                            isActive
                              ? 'bg-primary text-white shadow-primary'
                              : 'text-gray-text hover:text-white hover:bg-dark-lighter'
                          }`
                        }
                      >
                        <item.icon className="w-5 h-5" />
                        <span className="font-medium flex-1">{item.name}</span>
                        {item.href === '/dynamic-content' && (readyToPublish ?? 0) > 0 && (
                          <span
                            title="Approved revisions ready for you to publish"
                            className="min-w-[20px] px-1.5 py-0.5 rounded-full bg-green-500/20 text-green-400 text-xs font-medium text-center"
                          >
                            {readyToPublish}
                          </span>
                        )}
                        {badge && badge.count > 0 && (
                          <span className="min-w-[20px] px-1.5 py-0.5 rounded-full bg-primary/20 text-primary text-xs font-medium text-center">
                            {badge.count}
                            {badge.truncated ? '+' : ''}
                          </span>
                        )}
                      </NavLink>
                    );
                  })}
                </div>
              );
            })}
          </nav>

          {/* Footer */}
          <div className="p-6 border-t border-gray-border">
            <div className="text-xs text-gray-text">
              <p className="mb-1">Version 1.0.0</p>
              <p>© 2025 DonateMate</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 h-16 bg-dark-card border-b border-gray-border backdrop-blur-sm">
          <div className="flex items-center justify-between h-full px-6">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-gray-text hover:text-white"
            >
              <Menu className="w-6 h-6" />
            </button>

            <div className="flex-1 flex items-center justify-end space-x-4">
              {/* Status indicator */}
              <div className="flex items-center space-x-2 text-sm">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span className="text-gray-text hidden sm:inline">System Online</span>
              </div>

              {/* User menu */}
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-dark-lighter transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                    <User className="w-4 h-4 text-primary" />
                  </div>
                  <span className="text-white text-sm hidden sm:inline">{user?.name}</span>
                </button>

                {userMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} />
                    <div className="absolute right-0 mt-2 w-56 glass-card p-2 z-50 animate-fade-in">
                      <div className="px-3 py-2 border-b border-gray-border mb-2">
                        <p className="text-sm font-medium text-white">{user?.name}</p>
                        <p className="text-xs text-gray-text">{user?.email}</p>
                      </div>
                      <button
                        onClick={handleSignOut}
                        className="w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-gray-text hover:text-white hover:bg-dark-lighter transition-colors"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>Sign Out</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
